/**
 * ControlSection — the next checkpoint, and why there has to be one.
 *
 * This is the piece the product has never had: a stated date when the
 * account gets looked at again, and a list of what should be different by
 * then. Without it a report is a one-off opinion — the next campaign launch
 * undoes the fixes and nobody notices until the following audit.
 *
 * The expectations are the section's substance, so they are cards rather
 * than list rows: each one is a small before/after scorecard the reader can
 * hold the team to, and a wall of them is what makes the checkpoint feel
 * like a commitment instead of a footnote.
 *
 * Staying subscribed to that checkpoint is a separate step and lives in
 * `NotificationsSection`: the review call treated control and the call to
 * action as two stages of the journey, not one.
 *
 * @format
 */

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Icon} from 'ui/components/atoms/Icon';
import {Row} from 'ui/components/layout/Row';
import {Stack} from 'ui/components/layout/Stack';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

import {expectationMetricLabel} from '../content';
import {useReportFormat} from '../format';
import {DAYS, MEASURE, MEASURE_WIDE, daysBetween, pluralize} from '../helpers';
import type {Control, Expectation, IsoDate, KpiName} from '../types';
import {SplitCard, SplitPanel} from './SplitCard';

export interface ControlSectionProps {
  control: Control;
  /** Дата съёма данных: от неё, а не от «сегодня», считается срок. */
  dataDate: IsoDate;
  /** Метрика медиаплана — по ней подписывается ожидание с `metric: KPI`. */
  kpiName?: KpiName | null;
}

/**
 * Дата стоит в самом заголовке этажа, а не под ним отдельной цифрой.
 *
 * Отдельным блоком она требовала подписи — «дата, когда мы посмотрим
 * кабинет заново», — то есть объясняла сама себя. В строке «Следующая точка
 * контроля — 1 августа 2026» подпись уже произнесена заголовком, и число
 * читается сразу.
 *
 * Кегль тот же, что у заголовка: это одна фраза, а не заголовок с довеском.
 * Цвет — primary: единственное, чем дата отличается, это что она факт, а не
 * название раздела.
 */
export const NextCheckpointDate = styled(Box)(({theme}) => ({
  ...text('heading.xl'),
  color: theme.palette.primary.main,
}));

const DaysLeft = styled(Box)({
  ...text('body.l'),
  color: surfaceVar('onSurfaceLight'),
});

/** Неразрывный пробел escape-ом: живой символ в JSX-детях валит линтер. */
const NBSP = '\u00A0';

/**
 * Довод блока: почему точка контроля вообще назначается.
 *
 * Строкой, а не JSX-текстом: внутри неразрывные пробелы, а
 * `no-irregular-whitespace` ловит их в детях разметки.
 */
const LEAD =
  `Кабинет${NBSP}— это динамично меняющаяся среда: каждый запуск и${NBSP}правки кампаний меняют картину. Разовый аудит фиксирует один день, а${NBSP}позже проблемы могут вернуться. Контроль работает, только когда${NBSP}он не${NBSP}прекращается и${NBSP}цикличен.`;

/** Вводный абзац этого блока — не сноска, а его аргумент. */
const Lead = styled(Box)({
  ...text('heading.m'),
  color: surfaceVar('onSurface'),
  maxWidth: MEASURE_WIDE,
});

const SubHeading = styled(Box)({
  ...text('heading.m'),
  color: surfaceVar('onSurface'),
});

const MetricName = styled(Box)({
  ...text('body.l'),
  color: surfaceVar('onSurfaceLight'),
});

const FromValue = styled(Box)({
  ...text('heading.m'),
  color: surfaceVar('onSurfaceMuted'),
  whiteSpace: 'nowrap',
});

const ToValue = styled(Box)(({theme}) => ({
  ...text('heading.m'),
  color: theme.palette.good.main,
}));

/** Пояснение к цели — не второстепенный текст, приглушать его нечего. */
const Note = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceLight'),
  maxWidth: MEASURE,
});

function ExpectationRow({item, kpiName}: {item: Expectation; kpiName?: KpiName | null}) {
  const fmt = useReportFormat();

  return (
    <SplitPanel padding='l'>
      <Stack gap='xs'>
        <MetricName>{expectationMetricLabel(item.metric, kpiName)}</MetricName>
        {/* Ключевая строка — сам переход. Она крупнее подписи над ней:
            читателю нужно «с 4 546 до 3 300», а не название метрики. */}
        <Row gap='s' alignY='baseline' wrap='wrap'>
          <FromValue>{fmt.target(item.from)}</FromValue>
          <Icon name='arrow_forward' size='s' color='on-surface-muted' />
          <ToValue>{fmt.target(item.to)}</ToValue>
        </Row>
        {item.note && <Note>{item.note}</Note>}
      </Stack>
    </SplitPanel>
  );
}

export function ControlSection({control, dataDate, kpiName}: ControlSectionProps) {
  // Срок считается от даты съёма данных, а не от «сегодня»: отчёт читают и
  // через неделю после выпуска, и число дней в нём не должно уезжать.
  const daysLeft = Math.max(0, daysBetween(dataDate, control.nextReportDate));

  return (
    // Блок работает как обещание, а не как таблица: дата берёт весь вес,
    // остальное её обслуживает. Поэтому без плашек и без колонок.
    <Stack gap='xl'>
      {/* Сама дата стоит в заголовке этажа; здесь — только срок, который из
          неё следует. */}
      <DaysLeft>через {pluralize(daysLeft, DAYS)} — столько есть на исправление</DaysLeft>

      <Lead>{LEAD}</Lead>

      <Stack gap='l'>
        <SubHeading>Что должно измениться к этой дате</SubHeading>
        <SplitCard direction='column' reflowAt='never'>
          {control.expectations.map(item => (
            <ExpectationRow key={item.metric} item={item} kpiName={kpiName} />
          ))}
        </SplitCard>
      </Stack>
    </Stack>
  );
}
